import {computed} from 'vue'
import {useLocalStorage} from '@vueuse/core'

/**
 * The one photo clipboard, shared by every gallery in every OMS window.
 *
 * A photo is one File, unique by id, so the clipboard carries the photos
 * themselves (id, url, thumbs - enough to show and to paste) plus where they
 * came from: the delete link of the gallery they were cut from, which is how a
 * cut takes them off it once they are pasted somewhere else. It lives in
 * localStorage, and @vueuse keeps every open window's copy in step, so a cut
 * in one tab pastes in another. The BroadcastChannel tells the other windows
 * when a gallery lost photos (a cut landed) so a stale view drops them too.
 */
const KEY = 'oms.photo-clipboard'
const EMPTY = () => ({mode: null, photos: [], from: null, at: 0})

const state = useLocalStorage(KEY, EMPTY(), {deep: true, mergeDefaults: true})

const channel = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('oms-photos') : null

// only what a gallery needs to show and paste a photo - never the whole record it rode in on
const slim = (p) => ({
  id: p.id,
  url: p.url,
  thumbs: p.thumbs,
  filename: p.filename,
  mime_type: p.mime_type,
  description: p.description,
  created_datetime: p.created_datetime,
  createdby: p.createdby ? {userid: p.createdby.userid} : undefined,
  _links: p._links && p._links.delete_url ? {delete_url: p._links.delete_url} : {},
})

const clipboard = {
  state,
  photos: computed(() => state.value.photos || []),
  count: computed(() => (state.value.photos || []).length),
  has: computed(() => !!(state.value.photos || []).length),
  isCut: computed(() => state.value.mode === 'cut'),
  from: computed(() => state.value.from),

  /** Put photos on the clipboard; `from` is what the gallery is called (Bid 23-00045, SKU ...). */
  set(mode, photos, from) {
    state.value = {mode, photos: photos.filter(p => !!p.id).map(slim), from: from || null, at: Date.now()}
  },

  copy(photos, from) {
    this.set('copy', photos, from)
  },

  /** A cut needs a way off the source gallery: only photos with a delete link can be cut. */
  cut(photos, from) {
    this.set('cut', photos.filter(p => p._links && p._links.delete_url), from)
  },

  clear() {
    state.value = EMPTY()
  },

  /**
   * Once a paste has gone through: a cut is spent (the photos are taken off
   * where they came from and the clipboard empties), a copy stays for the next
   * gallery. Returns the photos that were on it.
   */
  async consume() {
    const photos = [...this.photos.value]
    if (!this.isCut.value)
      return photos

    const removed = []
    for (const p of photos) {
      try {
        await window.vue.asyncAction(p._links.delete_url.href, p._links.delete_url.method, {noload: true})
        removed.push(p._links.delete_url.href)
      } catch (e) {
        window.vue.toast('Photo ' + p.id + ' was pasted but not removed from where it came from', true)
      }
    }
    this.clear()
    this.announceRemoved(removed)

    return photos
  },

  /**
   * Tell every gallery, in this window and the others, that these photos are
   * off a gallery - by their delete links, which name the gallery row (a File
   * on two galleries is only off the one it was deleted from).
   */
  announceRemoved(hrefs) {
    if (!hrefs.length)
      return
    const msg = {type: 'removed', hrefs}
    listeners.forEach(fn => fn(msg))
    if (channel)
      channel.postMessage(msg)
  },

  onMessage(fn) {
    listeners.add(fn)
    return () => listeners.delete(fn)
  },
}

const listeners = new Set()
if (channel)
  channel.onmessage = (e) => listeners.forEach(fn => fn(e.data))

export default clipboard
